import React, { useState } from "react";
import { Link, useNavigate, useLocation } from "react-router-dom";
import {
  Box,
  AppBar,
  Toolbar,
  Tooltip,
  Button,
  Avatar,
  Typography,
  Tabs,
  Tab,
} from "@mui/material";
import DownloadIcon from "@mui/icons-material/Download";
import LoginIcon from "@mui/icons-material/Login";

import "./AppHeader.css";
import { ReactComponent as AppIcon } from "../../assets/ic_app_icon.svg";
import { useAuthStore } from "../../stores/useAuthStore";
import { useFirebaseStore } from "../../stores/useFirebaseStore";
import { ConfirmationDialog } from "../../components/common/ConfirmationDialog";
import { useLogin } from "../../hooks/useLogin";
import { usePWAInstall } from "../../hooks/usePWAInstall";
import { logCTAEvent } from "../../service/analytics";

const handleTabClick = (item: { label: string; to: string }) => {
  logCTAEvent("cta_nav_tab_click", {
    tab_name: item.label,
    path: item.to,
  });
};

export default function AppHeader({ children }: { children: React.ReactNode }) {
  const { watchListMovies, favoriteMovies } = useFirebaseStore();
  const { profile } = useAuthStore() as any;
  const { handleLogin, logout } = useLogin();
  const { isInstallable, handleInstallClick } = usePWAInstall();

  const [logoutDialogOpen, setLogoutDialogOpen] = useState(false);
  const navigate = useNavigate();
  const location = useLocation();

  const isLoggedIn = profile && Object.keys(profile).length > 0;

  const menuItems = [
    { label: "Popular", to: "/" },
    { label: "Top Rated", to: "/top-rated" },
    { label: "Upcoming", to: "/upcoming" },
    { label: "Search", to: "/search" },
    {
      label: `WatchList${watchListMovies.length > 0 ? ` (${watchListMovies.length})` : ""}`,
      to: "/watchlist",
    },
    {
      label: `Favourites${favoriteMovies.length > 0 ? ` (${favoriteMovies.length})` : ""}`,
      to: "/favourites",
    },
  ];

  const getActiveTab = () => {
    const index = menuItems.findIndex((item) => item.to === location.pathname);
    return index !== -1 ? index : false;
  };

  const activeTab = getActiveTab();

  const handleHeaderClick = () => {
    logCTAEvent("cta_nav_tab_click", {
      tab_name: "Popular",
      path: "/",
    });
    navigate("/");
  };

  const onLoginClick = () => {
    logCTAEvent("cta_login_click", {
      login_method: "google",
    });
    handleLogin();
  };


  const handleLogout = () => {
    setLogoutDialogOpen(true);
  };

  const confirmLogout = () => {
    logCTAEvent("cta_logout_click");
    logout();
    setLogoutDialogOpen(false);
  };

  const cancelLogout = () => {
    setLogoutDialogOpen(false);
  };

  const onPWAInstallClick = () => {
    logCTAEvent("cta_pwa_install", {
      platform: navigator.platform || "unknown",
    });
    handleInstallClick();
  };

  return (
    <Box
      sx={{
        minHeight: "100vh",
        display: "flex",
        flexDirection: "column",
        backgroundColor: "#ffffff",
      }}
    >
      <AppBar
        position="fixed"
        sx={{
          backgroundColor: "rgba(15, 23, 42, 0.95) !important",
          backdropFilter: "blur(12px)",
          WebkitBackdropFilter: "blur(12px)",
          borderBottom: "1px solid rgba(255, 255, 255, 0.08)",
          boxShadow: "0 4px 20px rgba(0, 0, 0, 0.2)",
          zIndex: (theme) => theme.zIndex.drawer + 1,
        }}
      >
        <Toolbar
          disableGutters
          sx={{
            px: { xs: 1, sm: 2.5 },
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            minHeight: { xs: 56, sm: 64 },
            gap: { xs: 0.5, sm: 1.5 },
          }}
        >
          {/* Logo & App Title */}
          <Box
            className="brand-container"
            onClick={handleHeaderClick}
            sx={{
              display: "flex",
              alignItems: "center",
              flexShrink: 0,
              cursor: "pointer",
              mr: { xs: 0.5, sm: 1 },
            }}
          >
            <AppIcon style={{ width: 32, height: 32, marginRight: 6 }} />
            <Typography
              variant="h6"
              component="h3"
              sx={{
                margin: 0,
                whiteSpace: "nowrap",
                fontFamily: "'Outfit', 'Inter', sans-serif",
                fontWeight: 800,
                fontSize: { xs: "0.95rem", sm: "1.15rem", md: "1.25rem" },
                display: { xs: "none", sm: "block" },
                letterSpacing: "-0.03em",
                background: "linear-gradient(90deg, #38bdf8, #818cf8)",
                WebkitBackgroundClip: "text",
                WebkitTextFillColor: "transparent",
              }}
            >
              My Movies List
            </Typography>
          </Box>

          {/* Navigation Top Tabs */}
          <Box
            sx={{
              flex: 1,
              minWidth: 0,
              display: "flex",
              justifyContent: "center",
              overflow: "hidden",
            }}
          >
            <Tabs
              value={activeTab}
              variant="scrollable"
              scrollButtons="auto"
              allowScrollButtonsMobile
              aria-label="movie app top navigation tabs"
              sx={{
                minHeight: 56,
                "& .MuiTabs-indicator": {
                  height: 3,
                  borderRadius: "3px 3px 0 0",
                  backgroundColor: "#38bdf8",
                  boxShadow: "0 0 10px #38bdf8",
                },
                "& .MuiTabs-scrollButtons": {
                  color: "rgba(255, 255, 255, 0.7)",
                  width: { xs: 24, sm: 32 },
                  "&.Mui-disabled": { opacity: 0.2 },
                },
              }}
            >
              {menuItems.map((item, index) => (
                <Tab
                  key={item.to}
                  component={Link}
                  to={item.to}
                  label={item.label}
                  value={index}
                  onClick={() => handleTabClick(item)}
                  sx={{
                    minHeight: 56,
                    textTransform: "none",
                    fontSize: { xs: "0.82rem", sm: "0.9rem" },
                    fontWeight: activeTab === index ? 700 : 500,
                    color: "rgba(255, 255, 255, 0.65)",
                    px: { xs: 1, sm: 1.8 },
                    minWidth: "auto",
                    whiteSpace: "nowrap",
                    transition: "all 0.2s ease",
                    "&.Mui-selected": {
                      color: "#38bdf8",
                    },
                    "&:hover": {
                      color: "white",
                      backgroundColor: "rgba(255, 255, 255, 0.04)",
                    },
                  }}
                />
              ))}
            </Tabs>
          </Box>

          {/* Right Action Section: Download & Auth Status */}
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              gap: { xs: 0.5, sm: 1.5 },
              flexShrink: 0,
            }}
          >
            {isInstallable && (
              <Tooltip title="Install App">
                <Button
                  onClick={onPWAInstallClick}
                  startIcon={<DownloadIcon />}
                  size="small"
                  sx={{
                    color: "white",
                    textTransform: "none",
                    fontSize: { xs: 12, sm: 13 },
                    fontWeight: 500,
                    borderRadius: "16px",
                    px: { xs: 1, sm: 1.5 },
                    minWidth: "auto",
                    backgroundColor: "rgba(255, 255, 255, 0.08)",
                    display: { xs: "none", md: "inline-flex" },
                    "&:hover": {
                      backgroundColor: "rgba(255, 255, 255, 0.15)",
                    },
                    whiteSpace: "nowrap",
                  }}
                >
                  Download
                </Button>
              </Tooltip>
            )}

            {isLoggedIn ? (
              <Tooltip title="Click to logout">
                <Box
                  onClick={handleLogout}
                  sx={{
                    display: "flex",
                    alignItems: "center",
                    gap: 1,
                    backgroundColor: "rgba(255, 255, 255, 0.08)",
                    px: { xs: 1, sm: 1.5 },
                    py: 0.5,
                    borderRadius: "20px",
                    border: "1px solid rgba(56, 189, 248, 0.3)",
                    cursor: "pointer",
                    transition: "all 0.2s ease",
                    "&:hover": {
                      backgroundColor: "rgba(248, 113, 113, 0.15)",
                      borderColor: "rgba(248, 113, 113, 0.5)",
                      transform: "translateY(-1px)",
                    },
                  }}
                >
                  <Avatar
                    src={profile.photoURL || ""}
                    alt={profile.displayName || "User"}
                    sx={{ width: { xs: 28, sm: 30 }, height: { xs: 28, sm: 30 }, border: "1.5px solid #38bdf8" }}
                  />
                  <Typography
                    variant="body2"
                    sx={{
                      fontWeight: 600,
                      color: "white",
                      fontSize: { xs: "0.8rem", sm: "0.85rem" },
                      display: { xs: "none", sm: "block" },
                      maxWidth: 120,
                      overflow: "hidden",
                      textOverflow: "ellipsis",
                      whiteSpace: "nowrap",
                    }}
                  >
                    {profile.displayName || "User"}
                  </Typography>
                </Box>
              </Tooltip>
            ) : (
              <Button
                onClick={onLoginClick}
                variant="contained"
                size="small"
                startIcon={<LoginIcon sx={{ fontSize: { xs: "1rem", sm: "1.1rem" } }} />}
                sx={{
                  background: "linear-gradient(90deg, #0284c7, #38bdf8)",
                  color: "white",
                  textTransform: "none",
                  borderRadius: "20px",
                  fontWeight: 600,
                  fontSize: { xs: "0.78rem", sm: "0.85rem" },
                  py: 0.5,
                  px: { xs: 1.5, sm: 2 },
                  minWidth: "auto",
                  boxShadow: "0 2px 8px rgba(56, 189, 248, 0.3)",
                  "&:hover": {
                    background: "linear-gradient(90deg, #0369a1, #0284c7)",
                  },
                }}
              >
                Login
              </Button>
            )}
          </Box>
        </Toolbar>
      </AppBar>


      <Box
        component="main"
        sx={{
          flex: 1,
          mt: { xs: "56px", sm: "64px" },
          p: { xs: 1.5, sm: 3 },
        }}
      >
        {children}
      </Box>

      <ConfirmationDialog
        open={logoutDialogOpen}
        onClose={cancelLogout}
        onConfirm={confirmLogout}
        title="Confirm Logout"
        content="Are you sure you want to logout?"
        confirmButtonLabel="Logout"
      />
    </Box>
  );
}
